import React from "react"
import { Box, Grid, Text } from "@chakra-ui/react"
import type { VariantType } from "types"
import { useCurrencySymbol } from "hooks"
import { getPrice } from "utils/getPrice"
import { useTypicalPrice } from "hooks/useTypicalPrice"
import { useTypicalJourney } from "packages/acquisition/components/GuestPrice/hooks/useTypicalJourney"
import { useNMPriceLabel } from "packages/acquisition/components/GuestPrice/hooks/useNMPriceLabel"
import {
  MembersPrice,
  NonMembersPrice,
  PriceCopy,
  primaryPriceStyle,
  secondaryPriceStyle,
} from "./ProductCardPricing.styles"

type ProductCardPricingVariantType = Pick<
  VariantType,
  "availability" | "pricing" | "pricingDetails"
>

type Props = {
  variant: ProductCardPricingVariantType
  isATBConfirmation?: boolean
}

export const ProductCardPricing: React.FC<Props> = ({
  variant,
  isATBConfirmation = false,
}) => {
  const currencySymbol = useCurrencySymbol()

  const { pricing, availability, pricingDetails } = variant
  const outOfStock = availability?.status === "outofstock"
  const isComingSoon = availability?.label?.toLowerCase() === "coming soon"
  const showOutOfStock = outOfStock && !isComingSoon

  const memberPrice = pricingDetails?.primary?.raw
    ? `${currencySymbol}${pricingDetails?.primary?.raw}`
    : getPrice(pricing.members, currencySymbol)

  const computedSecondaryPrice = useTypicalPrice(pricing)
  const secondaryPriceToDisplay = pricingDetails?.secondary?.raw
    ? `${currencySymbol}${pricingDetails?.secondary?.raw}`
    : getPrice(computedSecondaryPrice.typicalPrice, currencySymbol)

  const nmPriceLabel = useNMPriceLabel()
  const primaryLabel = pricingDetails?.primary?.label ?? "Members"
  const secondaryLabel = pricingDetails?.secondary?.label ?? nmPriceLabel

  const hasTypicalJourney = useTypicalJourney()

  const memberPriceStyle = hasTypicalJourney
    ? secondaryPriceStyle
    : primaryPriceStyle

  const nonMemberPriceStyle = hasTypicalJourney
    ? primaryPriceStyle
    : secondaryPriceStyle

  return (
    <Box
      mt={-3}
      opacity={showOutOfStock ? "50%" : ""}
      w="full"
      className="product-card-pricing"
      data-highlighted-price={hasTypicalJourney ? "secondary" : "primary"}
    >
      <Grid gridTemplateColumns={{ base: "1fr 1fr" }}>
        <MembersPrice
          data-testid="ProductCardMembersPriceText"
          variant="acuminLight"
          lineHeight={
            isATBConfirmation ? "1.25rem" : { base: "1.25rem", lg: "1.5rem" }
          }
          {...memberPriceStyle}
        >
          {memberPrice}
          <br />
          <PriceCopy
            as="span"
            fontSize={
              isATBConfirmation ? "0.625rem" : { base: "0.625rem", md: 1 }
            }
          >
            {primaryLabel}
          </PriceCopy>
        </MembersPrice>
        <NonMembersPrice
          data-testid="ProductCardTypicalPriceText"
          variant="acuminLight"
          lineHeight={
            isATBConfirmation ? "1.25rem" : { base: "1.25rem", lg: "1.5rem" }
          }
          {...nonMemberPriceStyle}
        >
          <Text as="span" textDecoration="none">
            {secondaryPriceToDisplay}
          </Text>
          <br />
          <PriceCopy
            as="span"
            fontSize={
              isATBConfirmation ? "0.625rem" : { base: "0.625rem", md: 1 }
            }
          >
            {secondaryLabel}
          </PriceCopy>
        </NonMembersPrice>
      </Grid>
    </Box>
  )
}
